<template>
  <generic-modal
    @close="closeModal"
    @submit="linkAddress"
    :title="`Link ${props.tenantDetail.login_name || props.tenantDetail.name} to address`"
    width="600px"
    :disabled="!selectedAddress || isLinking"
  >
    <div>The change will be reflected in eleena in 1-2 days</div>
    <form-field
      label="Kies adres"
      placeholder="Zoek naar een adres"
      :options="addresses"
      type="multi"
      v-model="selectedAddress"
      @search-change="handleSearchChange"
      class="tw-w-[250px] tw-mr-5"
    />
  </generic-modal>
</template>

<script setup>
import { ref, computed, shallowRef } from 'vue'
import { refDebounced } from '@vueuse/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { notifications } from '@/util'
import fetchData from '@/util/fetch'
import FormField from '@/components/shared/FormField.vue'
import GenericModal from '@/components/shared/GenericModal.vue'

const queryClient = useQueryClient()
const emit = defineEmits(['close'])

const props = defineProps({
  tenantDetail: {
    type: Object,
    required: true,
  },
})

const selectedAddress = ref(null)
const searchInput = shallowRef('')
const query = refDebounced(searchInput, 500)

const handleSearchChange = (searchQuery) => {
  searchInput.value = searchQuery
}

const { data: addressesQuery } = useQuery({
  queryKey: computed(() => ['addresses', query.value]),
  queryFn: async () => {
    if (!query.value || query.value.length < 2) return []

    try {
      const urlParams = new URLSearchParams({ search: query.value, limit: 5 })
      const result = await fetchData(`/eleena/api/v1/eleena-addresses?${urlParams}`)

      if (!result || !result.items || !Array.isArray(result.items)) return []

      return result.items.map((address) => ({
        value: address.address_id,
        label: `${address.street} ${address.house_number}, ${address.zip_code} ${address.zip_code_ext}`,
      }))
    } catch (error) {
      notifications.add(`Error fetching addresses:: ${error.message}`, 'error')
      throw new Error('Error fetching addresses:', error)
    }
  },
  refetchOnWindowFocus: false,
})

const addresses = computed(() => addressesQuery.value || [])

const { mutate: linkAddress, isLoading: isLinking } = useMutation({
  mutationFn: async () => {
    if (!selectedAddress.value) return

    const payload = { address_id: selectedAddress.value }

    const result = await fetchData(
      `/eleena/api/v1/eleena-clients/${props.tenantDetail.client_id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    )

    return result
  },
  onSuccess: () => {
    notifications.addNotification({ message: 'Address linked successfully', type: 'success' })
    queryClient.invalidateQueries(['tenants'])
    emit('close')
  },
  onError: (error) => {
    notifications.addNotification({
      message: `Error linking address: ${error.message}`,
      type: 'danger',
    })
  },
})

const closeModal = () => {
  selectedAddress.value = null
  emit('close')
}
</script>
